import { AxiosError, type AxiosInstance } from 'axios'

import { env } from '@/src/config/env'

export type TNetworkErrorNotice = Pick<AxiosError, 'code' | 'message' | 'config'>

type TErrorCallback = (error: TNetworkErrorNotice) => void

const USER_ACTION_METHODS = new Set(['POST', 'PATCH'])

export const isUserActionNetworkError = (error: Pick<TNetworkErrorNotice, 'code' | 'config'>): boolean => {
  const method = error.config?.method?.toUpperCase()
  return error.code === 'ERR_NETWORK' && method !== undefined && USER_ACTION_METHODS.has(method)
}

let errorCounter = 0

const errorNetworkListeners: TErrorCallback[] = []

export const addErrorListener = (callback: TErrorCallback) => {
  errorNetworkListeners.push(callback)
  return () => {
    // Remove the callback from the listeners array
    errorNetworkListeners.splice(errorNetworkListeners.indexOf(callback), 1)
  }
}

/**
 * Notify all the registered error listeners with the given error.
 *
 * @param {object} error - An object with `code` and `message` properties.
 * @param {string} error.code - The error code.
 * @param {string} error.message - The error message.
 */
export const notifyListeners = (error: TNetworkErrorNotice) => {
  errorNetworkListeners.forEach((callback) => callback(error))
}

const blackListUrls = [/status/, /notifications/, /conversation/]

const doesNotMatchBlackListedUrls = (input: string): boolean => {
  return !blackListUrls.some((regex) => regex.test(input))
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

// Reads the Sentry XHR breadcrumb URL off the Axios request, with runtime guards.
const extractSentryUrl = (request: unknown): string | undefined => {
  if (!isObject(request)) return undefined
  const xhr = request.__sentry_xhr_v3__
  if (!isObject(xhr)) return undefined
  return typeof xhr.url === 'string' ? xhr.url : undefined
}

/**
 * Add an interceptor to the given Axios instance that listens for network errors.
 *
 * When such an error occurs, it notifies all the registered error listeners with
 * the given error.
 *
 * @see src/general/components/UserActionNetworkErrorToasts
 *
 * @param {object} axiosInstance - The Axios instance to add the interceptor to.
 */
export const setNetworkErrorInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(undefined, (error: Error) => {
    if (error instanceof AxiosError) {
      const request: unknown = error.request
      const url = extractSentryUrl(request)
      const notice: TNetworkErrorNotice = {
        code: error.code,
        message: error.message,
        config: error.config,
      }

      if (
        error.code === 'ERR_NETWORK' &&
        env.EXPO_PUBLIC_APP_BASE_URL &&
        typeof url === 'string' &&
        url.startsWith(env.EXPO_PUBLIC_APP_BASE_URL) &&
        doesNotMatchBlackListedUrls(url)
      ) {
        if (isUserActionNetworkError(notice)) {
          notifyListeners(notice)
        } else {
          errorCounter += 1
          if (errorCounter === 5) {
            notifyListeners(notice)
          }
        }
      } else {
        errorCounter = 0
      }
    }
    return Promise.reject(error)
  })
}
